import { Component, OnInit, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DateIntervalFilterData, User } from '../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FuelReconKpiStats, GetFuelReconKpiSummaryRequest, GetFuelReconKpiSummaryResponse } from '../../../models/fuel-recon-kpi.model';
import { FuelReconciliationService } from '../../../services/fuel-reconciliation.service';
import { formatDropdownValue, getEnumFromString, formatCost, formatCostDifference } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-recon-kpi-widgets',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoDirective],
  templateUrl: './fuel-recon-kpi-widgets.component.html',
  styleUrl: './fuel-recon-kpi-widgets.component.scss'
})
export class FuelReconKpiWidgetsComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  public isLoading = false;
  public isUploading = false;
  public activeTab: string | 'fuelToReconcile' | 'reconciledSlips' = 'fuelToReconcile';
  public hasError: boolean = false;
  public currencyCode: string = '';
  public currencySymbol: string = '';
  public errorMessage: string;
  public dateFilter: DateIntervalFilterData;
  private User: User;
  private destroySubject: Subject<void> = new Subject<void>();
  protected cancelSubject = new Subject<void>();
  public unreconciledKpiSummary: FuelReconKpiStats = {
    value: 0,
    differenceInPercentage: 0,
    totalCost: 0,
    totalCostDifference: 0,
  };
  public fuelSlipKpiSummary: FuelReconKpiStats = {
    value: 0,
    differenceInPercentage: 0,
    totalCost: 0,
    totalCostDifference: 0,
  };
  public fuelProbeKpiSummary: FuelReconKpiStats = {
    value: 0,
    differenceInPercentage: 0,
    totalCost: 0,
    totalCostDifference: 0,
  };
  public varianceKpiSummary: FuelReconKpiStats = {
    value: 0,
    differenceInPercentage: 0,
    totalCost: 0,
    totalCostDifference: 0,
  };

  constructor(
    private fuelReconService: FuelReconciliationService,
    private errorService: ErrorHandlerService,
    private authService: AuthService,
    private filterService: FilterService
  ) {
    this.User = this.authService.getUser();
  }

  ngOnInit(): void {
    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (filter) => {
        if (!isEmptyObject(filter)) {
          this.dateFilter = filter;
          await this.getKpiCardsSummaryDataByAccountId();
          this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
          this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
        }
      });
  }

  public async getKpiCardsSummaryDataByAccountId(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.cancelSubject.next();
      const res = await this.fuelReconService.getKpiSummary(
        this.setupGetFuelReconKpiSummaryRequest(),
        this.cancelSubject
      );
      const data = res.data as GetFuelReconKpiSummaryResponse;
      this.unreconciledKpiSummary = data.unreconciledKpiSummary;
      this.fuelSlipKpiSummary = data.fuelSlipKpiSummary;
      this.fuelProbeKpiSummary = data.fuelProbeKpiSummary;
      this.varianceKpiSummary = data.varianceKpiSummary;
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  absValue(value: number): string {
    return Math.abs(value).toLocaleString();
  }

  formatGenericValue(
    value: number,
    unit: 'currency' | 'liters' | 'percent' = 'currency'
  ): string {
    if (value === undefined || value === null) {
      return '-';
    }
    switch (unit) {
      case 'currency':
        return `${this.currencySymbol}${value.toLocaleString()}`;
      case 'liters':
        return `${value.toLocaleString()} L`;
      case 'percent':
        return `${value.toLocaleString()}%`;
      default:
        return value.toLocaleString();
    }
  }

  formatUnreconciledValue(value: number): string {
    return formatDropdownValue(value, 'blankUnit', [{ value: 'blankUnit', unit: ' ' }], getEnumFromString);
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, '', [], getEnumFromString, this.currencySymbol);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.transloco.translate('kpi.periods.lastMonthHeading'), this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  formatUnreconciledDifference(value: number): string {
    return formatCostDifference(value, this.transloco.translate('kpi.periods.lastMonthHeading'), this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrend(): boolean {
    return isUptrendPositive(getEnumFromString('CostPerUnit'));
  }

  private setupGetFuelReconKpiSummaryRequest(): GetFuelReconKpiSummaryRequest {
    const currentTimeFrom = this.dateFilter.startDate;
    const currentTimeTo = this.dateFilter.endDate;

    const previousTimeFrom = this.dateFilter.previousCycleStartDate;
    const previousTimeTo = this.dateFilter.previousCycleEndDate;

    return {
      CurrentTimeFrom: currentTimeFrom,
      CurrentTimeTo: currentTimeTo,
      PreviousTimeFrom: previousTimeFrom,
      PreviousTimeTo: previousTimeTo,
      UserId: this.User.id,
    };
  }
}
